import { request } from "~/utils/request";
import {
    GET_COMPANIES_BY_DISCLOSURE_API,
    GET_FULLDOCS_COMPANIES_API,
    GRAPHICS_API,
    REPORTS_API,
    SOLR_SEARCH_API
} from "../routes";

import { SupportedLanguages, Language, Locale } from "~/providers/LanguageProvider";
import { SolRSearchData } from "~/features/Search/Results/types";
import { AxiosResponse } from "axios";

import {
    DetailsResponseItem,
    SolrSearchRegion,
    SolrSearchTypes,
    SolrSearchType,
    SolrMetaGraphicsParams
} from "~/types/search.types";
import { ResAllCompanies, ResFilters } from "~/types/filters.types";

interface IFetchSolsSearchMeta {
    data?: SolrMetaGraphicsParams;
    language: SupportedLanguages;
    searchType: SolrSearchTypes;
}

export interface IFetchDocumentsMeta {
    DisclosureType: number[];
    SearchText: string;
}

type SolrResponseCustom = {
    response: SolRSearchData;
};

/**
 * Fetches the (Disclosure, Categories, Topics) metadata for Graphics-type documents
 * @param data
 * @param language
 * @returns
 */
export const fetchGraphicsMeta = (
    data?: IFetchDocumentsMeta,
    language: SupportedLanguages = Language.EN
): Promise<AxiosResponse<DetailsResponseItem[], any>> => {
    return request({
        url: GRAPHICS_API,
        data,
        method: "POST",
        headers: {
            Culture: language === Language.EN ? Locale.US : Locale.FR
        }
    });
};

/**
 * Fetches the (Disclosure, Categories, Topics) metadata for Reports-type documents
 * @param data
 * @param language
 * @returns
 */
export const fetchReportsMeta = (
    data?: IFetchDocumentsMeta,
    language: SupportedLanguages = Language.EN
): Promise<AxiosResponse<DetailsResponseItem[], any>> => {
    return request({
        url: REPORTS_API,
        data,
        method: "POST",
        headers: {
            Culture: language === Language.EN ? Locale.US : Locale.FR
        }
    });
};

/**
 * Fetches all companies with available full documents
 * @returns
 */
export const fetchCompanies = (): Promise<AxiosResponse<ResAllCompanies, any>> => {
    return request({
        url: GET_FULLDOCS_COMPANIES_API,
        method: "GET"
    });
};

/**
 * Fetches the Company filter selections for the Full documents
 * @returns
 */
export const fetchCompanyFilters = (
    disclosureID: number,
    langLocale: string
): Promise<AxiosResponse<ResFilters, any>> => {
    return request({
        url: GET_COMPANIES_BY_DISCLOSURE_API,
        method: "POST",
        data: { DocumentTypeID: [disclosureID], LanguageCode: langLocale }
    });
};

export const fetchSolrSearchMeta = (
    params: IFetchSolsSearchMeta
): Promise<AxiosResponse<SolrResponseCustom, any>> => {
    const { data, language, searchType } = params;
    const region = import.meta.env.VITE_REGION as SupportedLanguages;

    const route: SolrSearchRegion =
        searchType === SolrSearchType.IMAGE
            ? `solr-${SolrSearchType.IMAGE}-${region}-new`
            : `solr-${SolrSearchType.PAGE}-${region}-new`;

    return request({
        url: SOLR_SEARCH_API(route),
        data,
        method: "POST",
        headers: {
            culture: language === Language.EN ? Locale.US : Locale.FR
        }
    });
};
